import { lazy } from "react";

import { ElementDetails } from "@/lib/types";

import { details as advancedCardDetails } from "@/components/elements/Advanced/Card/details";
import { details as advancedChartDetails } from "@/components/elements/Advanced/Chart/details";
import { details as advancedTableDetails } from "@/components/elements/Advanced/Table/details";
import { details as agendaDetails } from "@/components/elements/Calendar/agenda.details";
import { details as appDetails } from "@/components/elements/App/details";
import { details as bitrix24CalendarDetails } from "@/components/elements/Bitrix24/Calendar/details";
import { details as bitrix24WorkReportsDetails } from "@/components/elements/Bitrix24/WorkReports/details";
import { details as bookmarksDetails } from "@/components/elements/Bookmarks.details";
import { details as calendarDetails } from "@/components/elements/Calendar/details";
import { details as columnDetails } from "@/components/elements/Column.details";
import { details as countdownDetails } from "@/components/elements/Countdown/details";
import { details as counterDetails } from "@/components/elements/Counter/details";
import { details as githubActionsDetails } from "@/components/elements/Github/Actions/details";
import { details as githubIssuesDetails } from "@/components/elements/Github/Issues/details";
import { details as githubPersonalNotificationsDetails } from "@/components/elements/Github/Personal/Notifications/details";
import { details as githubPullRequestsDetails } from "@/components/elements/Github/PullRequests/details";
import { details as gitlabMergeRequestsDetails } from "@/components/elements/Gitlab/MergeRequests/details";
import { details as gitlabPipelinesDetails } from "@/components/elements/Gitlab/Pipelines/details";
import { details as googleEmailsDetails } from "@/components/elements/Google/Emails/details";
import { details as googleWorkspaceUsersDetails } from "@/components/elements/Google/WorkspaceUsers/details";
import { details as greetingDetails } from "@/components/elements/Greetings.details";
import { details as gridDetails } from "@/components/elements/Grid/details";
import { details as hackernewsDetails } from "@/components/elements/Hackernews/details";
import { details as jiraActiveSprintDetails } from "@/components/elements/Jira/ActiveSprint/details";
import { details as jiraIssuesDetails } from "@/components/elements/Jira/Issues/details";
import { details as linearIssuesDetails } from "@/components/elements/Linear/Issues/details";
import { details as meetingsDetails } from "@/components/elements/Meetings/details";
import { details as notesDetails } from "@/components/elements/Notes/details";
import { details as pinterestBoardsDetails } from "@/components/elements/Pinterest/Boards/details";
import { details as pomodoroDetails } from "@/components/elements/Pomodoro/details";
import { details as profileSelectorDetails } from "@/components/elements/ProfileSelector/details";
import { details as quotesDetails } from "@/components/elements/Quotes.details";
import { details as rootDetails } from "@/components/elements/Root.details";
import { details as rowDetails } from "@/components/elements/Row.details";
import { details as searchDetails } from "@/components/elements/Search.details";
import { details as slackActivityDetails } from "@/components/elements/Slack/Activity/details";
import { details as slackDndDetails } from "@/components/elements/Slack/Dnd/details";
import { details as slackFilesDetails } from "@/components/elements/Slack/Files/details";
import { details as slackPresenceDetails } from "@/components/elements/Slack/Presence/details";
import { details as slackSearchDetails } from "@/components/elements/Slack/Search/details";
import { details as slackStatusDetails } from "@/components/elements/Slack/Status/details";
import { details as stickyNotesDetails } from "@/components/elements/StickyNote/details";
import { details as timeDetails } from "@/components/elements/Time/details";
import { details as timerDetails } from "@/components/elements/Timer/details";
import { details as todoListDetails } from "@/components/elements/Todolist/details";
import { details as togglTimerDetails } from "@/components/elements/Toggl/Timer/details";
import { details as trelloAssignmentsDetails } from "@/components/elements/Trello/Assignments/details";
import { details as weatherDetails } from "@/components/elements/Weather/details";

// Widget components are code-split: this module stays light (static `details`
// metadata for pickers/menus/migrations) while each component chunk loads on
// first render via React.lazy. Renders are wrapped in <Suspense> by UIElement
// and the grid item (see UIElement.tsx / Grid/index.tsx).
const widget = (
  details: ElementDetails<any>,
  load: () => Promise<{ default: React.ComponentType<any> }>,
) => Object.assign(lazy(load), { details });

// Get disabled widgets from environment variable
const disabledWidgets = import.meta.env.VITE_DISABLED_WIDGETS?.split(",") || [];

// Filter out disabled widgets from the Elements object
const filteredElements = {
  Meetings: widget(meetingsDetails, () => import("./Meetings")),
  Root: widget(rootDetails, () => import("./Root")),
  Column: widget(columnDetails, () => import("./Column")),
  Row: widget(rowDetails, () => import("./Row")),
  Grid: widget(gridDetails, () => import("./Grid")),
  App: widget(appDetails, () => import("./App")),
  ProfileSelector: widget(
    profileSelectorDetails,
    () => import("./ProfileSelector"),
  ),
  Notes: widget(notesDetails, () => import("./Notes")),
  TodoList: widget(todoListDetails, () => import("./Todolist")),
  Time: widget(timeDetails, () => import("./Time")),
  Bookmarks: widget(bookmarksDetails, () => import("./Bookmarks")),
  Agenda: widget(agendaDetails, () => import("./Calendar/agenda")),
  Calendar: widget(calendarDetails, () => import("./Calendar")),
  Counter: widget(counterDetails, () => import("./Counter")),
  Countdown: widget(countdownDetails, () => import("./Countdown")),
  Timer: widget(timerDetails, () => import("./Timer")),
  Greeting: widget(greetingDetails, () => import("./Greetings")),
  StickyNotes: widget(stickyNotesDetails, () => import("./StickyNote")),
  Pomodoro: widget(pomodoroDetails, () => import("./Pomodoro")),
  Search: widget(searchDetails, () => import("./Search")),
  Quotes: widget(quotesDetails, () => import("./Quotes")),
  Weather: widget(weatherDetails, () => import("./Weather")),
  AdvancedTable: widget(advancedTableDetails, () => import("./Advanced/Table")),
  AdvancedChart: widget(advancedChartDetails, () => import("./Advanced/Chart")),
  AdvancedCard: widget(advancedCardDetails, () => import("./Advanced/Card")),
  GoogleEmails: widget(googleEmailsDetails, () => import("./Google/Emails")),
  GoogleWorkspaceUsers: widget(
    googleWorkspaceUsersDetails,
    () => import("./Google/WorkspaceUsers"),
  ),
  TogglTimer: widget(togglTimerDetails, () => import("./Toggl/Timer")),
  GithubPullRequests: widget(
    githubPullRequestsDetails,
    () => import("./Github/PullRequests"),
  ),
  GithubIssues: widget(githubIssuesDetails, () => import("./Github/Issues")),
  GithubActions: widget(githubActionsDetails, () => import("./Github/Actions")),
  GithubPersonalNotifications: widget(
    githubPersonalNotificationsDetails,
    () => import("./Github/Personal/Notifications"),
  ),
  GitlabMergeRequests: widget(
    gitlabMergeRequestsDetails,
    () => import("./Gitlab/MergeRequests"),
  ),
  GitlabPipelines: widget(
    gitlabPipelinesDetails,
    () => import("./Gitlab/Pipelines"),
  ),
  TrelloAssignments: widget(
    trelloAssignmentsDetails,
    () => import("./Trello/Assignments"),
  ),
  JiraActiveSprint: widget(
    jiraActiveSprintDetails,
    () => import("./Jira/ActiveSprint"),
  ),
  JiraIssues: widget(jiraIssuesDetails, () => import("./Jira/Issues")),
  PinterestBoards: widget(
    pinterestBoardsDetails,
    () => import("./Pinterest/Boards"),
  ),
  Hackernews: widget(hackernewsDetails, () => import("./Hackernews")),
  LinearIssues: widget(linearIssuesDetails, () => import("./Linear/Issues")),
  Bitrix24WorkReports: widget(
    bitrix24WorkReportsDetails,
    () => import("./Bitrix24/WorkReports"),
  ),
  Bitrix24Calendar: widget(
    bitrix24CalendarDetails,
    () => import("./Bitrix24/Calendar"),
  ),
  SlackActivity: widget(slackActivityDetails, () => import("./Slack/Activity")),
  SlackFiles: widget(slackFilesDetails, () => import("./Slack/Files")),
  SlackSearch: widget(slackSearchDetails, () => import("./Slack/Search")),
  SlackStatus: widget(slackStatusDetails, () => import("./Slack/Status")),
  SlackDnd: widget(slackDndDetails, () => import("./Slack/Dnd")),
  SlackPresence: widget(slackPresenceDetails, () => import("./Slack/Presence")),
};

export const Elements: Record<string, any> = Object.fromEntries(
  Object.entries(filteredElements).filter(
    ([key]) => !disabledWidgets.includes(key),
  ),
);

export const PremiumWidgets = [
  Elements.GithubActions,
  Elements.Hackernews,
  Elements.SlackActivity,
  Elements.SlackFiles,
  Elements.SlackSearch,
  Elements.SlackStatus,
  Elements.SlackDnd,
  Elements.SlackPresence,
].map(el => el.details.name);
